import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CampaignStatusBadge } from "@/components/campaigns/CampaignStatusBadge";
import { Button } from "@/components/ui/button";
import { useCampaignStore } from "@/store/campaignStore";
import { EmailCampaignStatus, EmailCampaignType } from "@/types/email";

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getTypeLabel(type: EmailCampaignType): string {
  switch (type) {
    case EmailCampaignType.Promotional: return "Promotional";
    case EmailCampaignType.Transactional: return "Transactional";
    case EmailCampaignType.Newsletter: return "Newsletter";
    default: return type;
  }
}

export default function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { currentCampaign, isLoading, isSaving, error, fetchCampaign, sendNow, retryCampaign } = useCampaignStore();

  useEffect(() => {
    if (campaignId) fetchCampaign(campaignId);
  }, [campaignId, fetchCampaign]);

  const handleSendNow = async () => {
    if (!currentCampaign) return;
    await sendNow(currentCampaign.id);
  };

  const handleRetry = async () => {
    if (!currentCampaign) return;
    await retryCampaign(currentCampaign.id);
  };

  if (isLoading) {
    return (
      <ProtectedRoute>
        <div className="lw-campaign-detail flex items-center justify-center py-24">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
            Loading…
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  if (error || !currentCampaign) {
    return (
      <ProtectedRoute>
        <div className="lw-campaign-detail container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="text-sm text-destructive">{error ?? "Campaign not found."}</p>
            <button
              onClick={() => navigate("/campaigns")}
              className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to Campaigns
            </button>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  const campaign = currentCampaign;
  const isDraft = campaign.status === EmailCampaignStatus.Draft;
  const isSent = campaign.status === EmailCampaignStatus.Sent;
  const isFailed = campaign.status === EmailCampaignStatus.Failed;

  return (
    <ProtectedRoute>
      <div className="lw-campaign-detail container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Back nav */}
          <button
            onClick={() => navigate("/campaigns")}
            className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Campaigns
          </button>

          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <CampaignStatusBadge status={campaign.status} />
                <span className="text-xs text-muted-foreground">{getTypeLabel(campaign.type)}</span>
              </div>
              <h1 className="text-2xl font-bold text-foreground">{campaign.name}</h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Created {formatDate(campaign.createdAt)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isDraft && (
                <>
                  <Button variant="outline" onClick={() => navigate(`/campaigns/new?campaignId=${campaign.id}`)}>
                    Edit
                  </Button>
                  <Button onClick={handleSendNow} disabled={isSaving}>
                    {isSaving ? "Sending…" : "Send Now"}
                  </Button>
                </>
              )}
              {isFailed && (
                <Button onClick={handleRetry} disabled={isSaving}>
                  {isSaving ? "Retrying…" : "Retry"}
                </Button>
              )}
            </div>
          </div>

          {/* Detail Cards */}
          <div className="space-y-4">
            {/* Email Content */}
            <div className="rounded-lg border border-border bg-card p-5">
              <h2 className="text-sm font-semibold text-foreground mb-3">Email Content</h2>
              <div className="space-y-2 text-sm">
                <div className="flex gap-3">
                  <span className="w-24 shrink-0 text-muted-foreground">Subject</span>
                  <span className="text-foreground">{campaign.subject || <span className="italic text-muted-foreground">Not set</span>}</span>
                </div>
                {campaign.previewText && (
                  <div className="flex gap-3">
                    <span className="w-24 shrink-0 text-muted-foreground">Preview</span>
                    <span className="text-foreground">{campaign.previewText}</span>
                  </div>
                )}
                <div className="flex gap-3">
                  <span className="w-24 shrink-0 text-muted-foreground">Email</span>
                  <Link
                    to={`/campaigns/email/${campaign.emailDocumentId}`}
                    className="text-primary hover:underline"
                  >
                    Open in Email Builder →
                  </Link>
                </div>
              </div>
            </div>

            {/* Sender */}
            <div className="rounded-lg border border-border bg-card p-5">
              <h2 className="text-sm font-semibold text-foreground mb-3">Sender</h2>
              <div className="space-y-2 text-sm">
                <div className="flex gap-3">
                  <span className="w-24 shrink-0 text-muted-foreground">From</span>
                  <span className="text-foreground">{campaign.fromName} &lt;{campaign.fromEmail}&gt;</span>
                </div>
                {campaign.replyToEmail && (
                  <div className="flex gap-3">
                    <span className="w-24 shrink-0 text-muted-foreground">Reply-To</span>
                    <span className="text-foreground">{campaign.replyToEmail}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Recipients */}
            <div className="rounded-lg border border-border bg-card p-5">
              <h2 className="text-sm font-semibold text-foreground mb-3">Recipients</h2>
              <div className="space-y-2 text-sm">
                <div className="flex gap-3">
                  <span className="w-24 shrink-0 text-muted-foreground">Estimated</span>
                  <span className="text-foreground">{campaign.estimatedRecipients.toLocaleString()}</span>
                </div>
                {isSent && (
                  <div className="flex gap-3">
                    <span className="w-24 shrink-0 text-muted-foreground">Sent to</span>
                    <span className="text-foreground">{campaign.actualRecipientsSent.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex gap-3">
                  <span className="w-24 shrink-0 text-muted-foreground">Tracking</span>
                  <span className="text-foreground">
                    {[campaign.trackOpens && "Opens", campaign.trackClicks && "Clicks"].filter(Boolean).join(", ") || "None"}
                  </span>
                </div>
              </div>
            </div>

            {/* Analytics (only when sent) */}
            {isSent && (
              <div className="rounded-lg border border-border bg-card p-5">
                <h2 className="text-sm font-semibold text-foreground mb-3">Results</h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <Stat
                    label="Sent"
                    value={campaign.actualRecipientsSent.toLocaleString()}
                  />
                  <Stat
                    label="Opens"
                    value={campaign.openCount.toLocaleString()}
                    rate={campaign.actualRecipientsSent > 0 ? campaign.openCount / campaign.actualRecipientsSent : 0}
                  />
                  <Stat
                    label="Clicks"
                    value={campaign.clickCount.toLocaleString()}
                    rate={campaign.actualRecipientsSent > 0 ? campaign.clickCount / campaign.actualRecipientsSent : 0}
                  />
                  <Stat
                    label="Bounces"
                    value={campaign.bounceCount.toLocaleString()}
                    rate={campaign.actualRecipientsSent > 0 ? campaign.bounceCount / campaign.actualRecipientsSent : 0}
                  />
                </div>
                {campaign.sentAt && (
                  <p className="mt-4 text-xs text-muted-foreground">Sent on {formatDate(campaign.sentAt)}</p>
                )}
              </div>
            )}

            {/* Scheduled time */}
            {campaign.status === EmailCampaignStatus.Scheduled && campaign.scheduledSendAt && (
              <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4">
                <p className="text-sm text-blue-500">
                  Scheduled for {formatDate(campaign.scheduledSendAt)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}

function Stat({ label, value, rate }: { label: string; value: string; rate?: number }) {
  return (
    <div className="rounded-md bg-muted/40 p-3 text-center">
      <p className="text-lg font-bold text-foreground">{value}</p>
      {rate !== undefined && (
        <p className="text-xs font-medium text-primary">{(rate * 100).toFixed(1)}%</p>
      )}
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
